import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Download, FileCheck, Paperclip, X } from 'lucide-react';
import { useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { AppModal } from '../../components/ui/app-modal';
import { Button } from '../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../components/ui/data-table';
import { usePermissions } from '../../hooks/use-permissions';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

type StudyReportStatus = 'DRAFT' | 'WAITING_FOR_APPROVAL' | 'APPROVED' | 'REJECTED';

interface StudyReportListItem {
  id: string;
  studyName: string | null;
  status: StudyReportStatus;
  cycleNumber: number;
  submittedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  attachments: { id: string }[];
  request: {
    id: string;
    referenceNo: string;
    status: string;
    account: { name: string };
  };
}

interface StudyReportAttachment {
  id: string;
  fileName: string;
  fileSizeBytes: number;
}

interface StudyReportApproval {
  id: string;
  decision: StudyReportStatus;
  comments: string | null;
  reviewedAt: string;
  reviewedBy: { id: string; name: string };
}

interface StudyReportDetail {
  id: string;
  studyName: string | null;
  comments: string | null;
  status: StudyReportStatus;
  cycleNumber: number;
  submittedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  attachments: StudyReportAttachment[];
  approvals: StudyReportApproval[];
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/study-reports/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('sm-study-report:view')) throw redirect({ to: '/dashboard' });
  },
  component: StudyReportsPage,
});

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CFG: Record<StudyReportStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Draft', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  WAITING_FOR_APPROVAL: {
    label: 'Waiting for Approval',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  APPROVED: { label: 'Approved', className: 'bg-success-50 text-success-600 border-success-200' },
  REJECTED: { label: 'Rejected', className: 'bg-error-50 text-error-600 border-error-200' },
};

function StatusBadge({ status }: { status: StudyReportStatus }) {
  const cfg = STATUS_CFG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function fmtBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

function StudyReportDetailModal({
  requestId,
  reportId,
  referenceNo,
  onClose,
  canApprove,
  canReject,
  onMutated,
}: {
  requestId: string;
  reportId: string;
  referenceNo: string;
  onClose: () => void;
  canApprove: boolean;
  canReject: boolean;
  onMutated: () => void;
}) {
  const qc = useQueryClient();
  const [decisionOpen, setDecisionOpen] = useState<'approve' | 'reject' | null>(null);
  const [comments, setComments] = useState('');

  const base = `study-requests/${requestId}/study-reports/${reportId}`;

  const { data: report, isLoading } = useQuery({
    queryKey: ['study-report-detail', requestId, reportId],
    queryFn: () => api.get(base).json<StudyReportDetail>(),
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      api.post(`${base}/approve`, { json: { comments: comments || undefined } }).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-study-reports'] });
      onMutated();
      onClose();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.post(`${base}/reject`, { json: { comments } }).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-study-reports'] });
      onMutated();
      onClose();
    },
  });

  async function handleDownload(attachmentId: string, fileName: string) {
    const res = await api
      .get(`${base}/attachments/${attachmentId}/download`)
      .json<{ url: string }>();
    const a = document.createElement('a');
    a.href = res.url;
    a.download = fileName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }

  const isWaiting = report?.status === 'WAITING_FOR_APPROVAL';

  return (
    <AppModal open onClose={onClose} title={referenceNo} maxWidth="max-w-2xl">
      {isLoading || !report ? (
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-10 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">
              {report.studyName || `Study Report #${report.cycleNumber}`}
            </p>
            <StatusBadge status={report.status} />
          </div>

          {report.comments && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Notes
              </p>
              <p className="text-sm leading-relaxed text-gray-700">{report.comments}</p>
            </div>
          )}

          {report.status === 'REJECTED' &&
            report.approvals.find((a) => a.decision === 'REJECTED') && (
              <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-error-500">
                  Rejection Reason
                </p>
                {(() => {
                  const rejection = report.approvals.find((a) => a.decision === 'REJECTED');
                  if (!rejection) return null;
                  return (
                    <>
                      <p className="text-sm leading-relaxed text-error-700">
                        {rejection.comments ?? '—'}
                      </p>
                      <p className="mt-1.5 text-xs text-error-400">
                        by {rejection.reviewedBy.name} · {fmt(rejection.reviewedAt)}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Attachments
              {report.attachments.length > 0 && (
                <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px]">
                  {report.attachments.length}
                </span>
              )}
            </p>
            {report.attachments.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                No attachments
              </p>
            ) : (
              <div className="space-y-1.5">
                {report.attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5"
                  >
                    <Paperclip size={12} className="shrink-0 text-gray-400" />
                    <span className="flex-1 truncate text-xs font-medium text-gray-700">
                      {att.fileName}
                    </span>
                    <span className="shrink-0 text-xs text-gray-400">
                      {fmtBytes(att.fileSizeBytes)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDownload(att.id, att.fileName)}
                      className="flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50"
                    >
                      <Download size={11} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>Created by {report.createdBy.name}</span>
            <span>·</span>
            <span>{fmt(report.createdAt)}</span>
            {report.submittedAt && (
              <>
                <span>·</span>
                <span>Submitted {fmt(report.submittedAt)}</span>
              </>
            )}
          </div>

          {report.approvals.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Approval History
              </p>
              <div className="space-y-2">
                {report.approvals.map((appr) => {
                  const isApproved = appr.decision === 'APPROVED';
                  return (
                    <div
                      key={appr.id}
                      className={cn(
                        'rounded-xl border px-4 py-3',
                        isApproved
                          ? 'border-success-200 bg-success-50'
                          : 'border-error-200 bg-error-50',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'text-xs font-semibold',
                            isApproved ? 'text-success-600' : 'text-error-600',
                          )}
                        >
                          {isApproved ? '✓ Approved' : '✗ Rejected'}
                        </span>
                        <span className="text-xs text-gray-400">{fmt(appr.reviewedAt)}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">by {appr.reviewedBy.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isWaiting && (canApprove || canReject) && (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              {decisionOpen && (
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder={
                    decisionOpen === 'reject'
                      ? 'Reason for rejection (required)…'
                      : 'Approval comments (optional)…'
                  }
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              )}
              <div className="flex justify-end gap-2">
                {decisionOpen ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDecisionOpen(null);
                        setComments('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={
                        (decisionOpen === 'reject' && !comments.trim()) ||
                        approveMutation.isPending ||
                        rejectMutation.isPending
                      }
                      onClick={() =>
                        decisionOpen === 'approve'
                          ? approveMutation.mutate()
                          : rejectMutation.mutate()
                      }
                      className={
                        decisionOpen === 'reject'
                          ? 'bg-error-500 hover:bg-error-600 focus:ring-error-200'
                          : 'bg-success-500 hover:bg-success-600 focus:ring-success-200'
                      }
                    >
                      {decisionOpen === 'approve'
                        ? approveMutation.isPending
                          ? 'Approving…'
                          : 'Confirm Approval'
                        : rejectMutation.isPending
                          ? 'Rejecting…'
                          : 'Confirm Rejection'}
                    </Button>
                  </>
                ) : (
                  <>
                    {canReject && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDecisionOpen('reject')}
                        className="border-error-200 text-error-600 hover:bg-error-50"
                      >
                        <X size={13} className="mr-1" />
                        Reject
                      </Button>
                    )}
                    {canApprove && (
                      <Button
                        size="sm"
                        onClick={() => setDecisionOpen('approve')}
                        className="bg-success-500 hover:bg-success-600 focus:ring-success-200"
                      >
                        <CheckCircle2 size={13} className="mr-1" />
                        Approve
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </AppModal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function StudyReportsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [selected, setSelected] = useState<{
    requestId: string;
    reportId: string;
    referenceNo: string;
  } | null>(null);

  // SM Consultant only: has study-report:approve/reject but NOT lab:create
  const canApprove = can('sm-study-report:approve') && !can('sm-lab:create');
  const canReject = can('sm-study-report:reject') && !can('sm-lab:create');

  const {
    data: reports = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['all-study-reports'],
    queryFn: () => api.get('study-reports').json<StudyReportListItem[]>(),
  });

  const columns: DataTableColumn<StudyReportListItem>[] = [
    {
      key: 'studyName',
      header: 'Report',
      render: (r) => (
        <button
          type="button"
          onClick={() =>
            setSelected({
              requestId: r.request.id,
              reportId: r.id,
              referenceNo: r.request.referenceNo,
            })
          }
          className="text-left text-sm font-medium text-gray-900 hover:text-brand-600 hover:underline"
        >
          {r.studyName || `Study Report #${r.cycleNumber}`}
        </button>
      ),
    },
    {
      key: 'referenceNo',
      header: 'Study Request',
      render: (r) => (
        <button
          type="button"
          onClick={() =>
            navigate({
              to: '/study-requests/$requestId',
              params: { requestId: r.request.id },
              search: { tab: 'study-report' },
            })
          }
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          {r.request.referenceNo}
        </button>
      ),
    },
    {
      key: 'account',
      header: 'Customer',
      render: (r) => <span className="text-sm text-gray-700">{r.request.account.name}</span>,
    },
    {
      key: 'attachments',
      header: 'Attachments',
      render: (r) => (
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <Paperclip size={11} className="shrink-0 text-gray-400" />
          {r.attachments.length}
        </span>
      ),
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      render: (r) => <span className="text-xs text-gray-500">{fmt(r.submittedAt)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ];

  return (
    <AppLayout>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50">
          <FileCheck size={20} className="text-brand-500" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Study Reports</h1>
          <p className="text-sm text-gray-400">All study reports across every study request</p>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={reports}
        keyField="id"
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No study reports found"
      />
      {selected && (
        <StudyReportDetailModal
          requestId={selected.requestId}
          reportId={selected.reportId}
          referenceNo={selected.referenceNo}
          onClose={() => setSelected(null)}
          canApprove={canApprove}
          canReject={canReject}
          onMutated={() => refetch()}
        />
      )}
    </AppLayout>
  );
}
